import { useEffect, useRef, ReactNode } from 'react'

// Skip link component for keyboard navigation
export function SkipLink({ href, children }: { href: string; children: ReactNode }) {
  return (
    <a
      href={href}
      className="sr-only focus:not-sr-only focus:absolute focus:top-4 focus:left-4 bg-blue-600 text-white px-4 py-2 rounded z-50 transition-all"
    >
      {children}
    </a>
  )
}

// Screen reader only content
export function ScreenReaderOnly({ children }: { children: ReactNode }) {
  return (
    <span className="sr-only">
      {children}
    </span>
  )
}

// ARIA live region for dynamic content updates
interface LiveRegionProps {
  children: ReactNode
  politeness?: 'off' | 'polite' | 'assertive'
  atomic?: boolean
  relevant?: 'additions' | 'removals' | 'text' | 'all'
}

export function LiveRegion({ 
  children, 
  politeness = 'polite', 
  atomic = true,
  relevant = 'all'
}: LiveRegionProps) {
  return (
    <div
      aria-live={politeness}
      aria-atomic={atomic}
      aria-relevant={relevant}
      className="sr-only"
    >
      {children}
    </div>
  )
}

// Focus trap component for modals and dialogs
interface FocusTrapProps {
  children: ReactNode
  disabled?: boolean
}

export function FocusTrap({ children, disabled = false }: FocusTrapProps) {
  const containerRef = useRef<HTMLDivElement>(null)
  const previousActiveElement = useRef<HTMLElement | null>(null)

  useEffect(() => {
    if (disabled) return

    const container = containerRef.current
    if (!container) return

    // Store the currently focused element
    previousActiveElement.current = document.activeElement as HTMLElement

    // Get all focusable elements within the container
    const getFocusableElements = () => {
      return container.querySelectorAll(
        'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
      ) as NodeListOf<HTMLElement>
    }

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key !== 'Tab') return

      const focusableElements = getFocusableElements()
      const firstElement = focusableElements[0]
      const lastElement = focusableElements[focusableElements.length - 1]

      if (event.shiftKey) {
        // Shift + Tab
        if (document.activeElement === firstElement) {
          event.preventDefault()
          lastElement?.focus()
        }
      } else {
        // Tab
        if (document.activeElement === lastElement) {
          event.preventDefault()
          firstElement?.focus()
        }
      }
    }

    // Focus the first focusable element
    const focusableElements = getFocusableElements()
    if (focusableElements.length > 0) {
      focusableElements[0].focus()
    }

    document.addEventListener('keydown', handleKeyDown)

    return () => {
      document.removeEventListener('keydown', handleKeyDown)
      // Restore focus to the previously focused element
      if (previousActiveElement.current) {
        previousActiveElement.current.focus()
      }
    }
  }, [disabled])

  if (disabled) {
    return <>{children}</>
  }

  return (
    <div ref={containerRef}>
      {children}
    </div>
  )
}

// Hook for managing focus when components mount/unmount
export function useFocusManagement() {
  const focusRef = useRef<HTMLElement>(null)

  const focusElement = (element?: HTMLElement | null) => {
    const targetElement = element || focusRef.current
    if (targetElement) {
      targetElement.focus()
    }
  }

  const handleKeyboardNavigation = (event: KeyboardEvent) => {
    // Handle Escape key
    if (event.key === 'Escape') {
      const activeElement = document.activeElement as HTMLElement
      if (activeElement && activeElement.blur) {
        activeElement.blur()
      }
    }

    // Handle Enter key on buttons and links
    if (event.key === 'Enter' || event.key === ' ') {
      const target = event.target as HTMLElement
      if (target.tagName === 'BUTTON' || target.role === 'button') {
        event.preventDefault()
        target.click()
      }
    }
  }

  useEffect(() => {
    document.addEventListener('keydown', handleKeyboardNavigation)
    return () => {
      document.removeEventListener('keydown', handleKeyboardNavigation)
    }
  }, [])

  return {
    focusRef,
    focusElement
  }
}

// Accessible button component with proper ARIA attributes
interface AccessibleButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: 'primary' | 'secondary' | 'danger'
  loading?: boolean
  loadingText?: string
  children: ReactNode
}

export function AccessibleButton({ 
  variant = 'primary',
  loading = false,
  loadingText = 'Loading...',
  disabled,
  children,
  className,
  ...props 
}: AccessibleButtonProps) {
  const baseClasses = 'px-4 py-2 rounded font-medium transition-colors focus:outline-none focus:ring-2 focus:ring-offset-2'
  const variantClasses = {
    primary: 'bg-blue-600 text-white hover:bg-blue-700 focus:ring-blue-500 disabled:bg-gray-300',
    secondary: 'bg-gray-200 text-gray-900 hover:bg-gray-300 focus:ring-gray-500 disabled:bg-gray-100',
    danger: 'bg-red-600 text-white hover:bg-red-700 focus:ring-red-500 disabled:bg-gray-300'
  }

  return (
    <button
      className={`${baseClasses} ${variantClasses[variant]} ${className || ''}`}
      disabled={disabled || loading}
      aria-disabled={disabled || loading}
      aria-describedby={loading ? `${props.id}-status` : undefined}
      {...props}
    >
      {loading ? (
        <>
          <span className="sr-only">{loadingText}</span>
          <span aria-hidden="true">{loadingText}</span>
        </>
      ) : (
        children
      )}
    </button>
  )
}

// Accessible form field wrapper
interface AccessibleFieldProps {
  id: string
  label: string
  required?: boolean
  error?: string
  description?: string
  children: ReactNode
}

export function AccessibleField({ 
  id, 
  label, 
  required = false, 
  error, 
  description, 
  children 
}: AccessibleFieldProps) {
  const errorId = `${id}-error`
  const descriptionId = `${id}-description`

  return (
    <div className="space-y-1">
      <label 
        htmlFor={id}
        className={`block text-sm font-medium ${required ? "after:content-['*'] after:text-red-500 after:ml-1" : ''}`}
      >
        {label}
      </label>
      
      {description && (
        <div id={descriptionId} className="text-sm text-gray-600">
          {description}
        </div>
      )}
      
      <div className="relative">
        {children}
      </div>
      
      {error && (
        <div
          id={errorId}
          className="text-sm text-red-600"
          role="alert"
          aria-live="polite"
        >
          {error}
        </div>
      )}
    </div>
  )
}

// Accessible table with proper headers and navigation
interface AccessibleTableProps {
  caption?: string
  headers: string[]
  children: ReactNode
}

export function AccessibleTable({ caption, headers, children }: AccessibleTableProps) {
  return (
    <div className="overflow-x-auto">
      <table className="min-w-full divide-y divide-gray-200" role="table">
        {caption && <caption className="sr-only">{caption}</caption>}
        <thead className="bg-gray-50">
          <tr>
            {headers.map((header, index) => (
              <th
                key={index}
                scope="col"
                className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
              >
                {header}
              </th>
            ))}
          </tr>
        </thead>
        <tbody className="bg-white divide-y divide-gray-200">
          {children}
        </tbody>
      </table>
    </div>
  )
}

// Hook for announcing content changes to screen readers
export function useAnnouncer() {
  const announcementRef = useRef<HTMLDivElement>(null)

  const announce = (message: string, priority: 'polite' | 'assertive' = 'polite') => {
    if (announcementRef.current) {
      // Clear previous announcement
      announcementRef.current.textContent = ''
      
      // Set new announcement with a slight delay to ensure it's picked up
      setTimeout(() => {
        if (announcementRef.current) {
          announcementRef.current.setAttribute('aria-live', priority)
          announcementRef.current.textContent = message
        }
      }, 100)
    }
  }

  const AnnouncementRegion = () => (
    <div
      ref={announcementRef}
      aria-live="polite"
      aria-atomic="true"
      className="sr-only"
    />
  )

  return {
    announce,
    AnnouncementRegion
  }
}

// Keyboard shortcut handler
export function useKeyboardShortcuts(shortcuts: Record<string, () => void>) {
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      const key = [
        event.ctrlKey && 'ctrl',
        event.altKey && 'alt',
        event.shiftKey && 'shift',
        event.metaKey && 'meta',
        event.key.toLowerCase()
      ].filter(Boolean).join('+')

      const handler = shortcuts[key]
      if (handler) {
        event.preventDefault()
        handler()
      }
    }

    document.addEventListener('keydown', handleKeyDown)
    return () => {
      document.removeEventListener('keydown', handleKeyDown)
    }
  }, [shortcuts])
}

// Color contrast checker utility
export function hasGoodContrast(foreground: string, background: string): boolean {
  // Simplified contrast checking - in a real app you'd use a more robust implementation
  // This is a placeholder for demonstration
  const getContrastRatio = (color1: string, color2: string): number => {
    // This would need a proper color contrast calculation
    // For now, return a mock value
    return 4.5 // WCAG AA standard minimum
  }
  
  return getContrastRatio(foreground, background) >= 4.5
}